/**
 * State Sync - handles profile synchronization and pending operations
 */

import type { Config } from '../modules/config';
import { getUserProfile, upsertUserProfile } from '../modules/auth';
import { showToast } from '../modules/ui';
import {
  STORAGE_KEYS,
  ensureSettings,
  saveSettings as saveSettingsToStorage,
} from '../modules/state';
import { applyServerSettings, type ServerDecisionSettings } from '../modules/settingsSync';
import type { PortalUser } from '../types';

/**
 * Apply profile settings from server to local state.
 * This ensures settings like timeZone, zipCode, and distanceUnits persist across devices.
 * Server values take precedence when available.
 */
export function applyProfileToLocalSettings(profile: PortalUser | null): boolean {
  if (!profile) return false;

  const settings = ensureSettings();
  let changed = false;

  // Apply timeZone from profile if available
  if (typeof profile.timeZone === 'string' && profile.timeZone) {
    if (settings.timeZone !== profile.timeZone) {
      settings.timeZone = profile.timeZone;
      changed = true;
    }
  }

  // Apply homePostalCode as zipCode if available
  if (typeof profile.homePostalCode === 'string' && profile.homePostalCode) {
    if (settings.zipCode !== profile.homePostalCode) {
      settings.zipCode = profile.homePostalCode;
      changed = true;
    }
  }

  // Apply server-backed decision settings (fees, target margin, notifications)
  if (profile.settings && typeof profile.settings === 'object') {
    if (applyServerSettings(settings, profile.settings as ServerDecisionSettings)) {
      changed = true;
    }
  }

  // Persist to localStorage so settings work offline and across page loads
  if (changed) {
    saveSettingsToStorage();
  }

  return changed;
}

/**
 * Apply pending profile updates from sessionStorage
 * Used after successful login to save profile data that was entered before auth
 */
export async function applyPendingProfileIfAny(cfg: Config | null): Promise<void> {
  if (!cfg) return;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.PENDING_PROFILE);
    if (!raw) return;
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_PROFILE);
    const pending = JSON.parse(raw);
    if (pending && pending.company) {
      try {
        const ok = await upsertUserProfile(cfg, { company: pending.company || '' });
        if (ok) {
          showToast('Profile updated', { variant: 'success', duration: 2500 });
        } else {
          showToast('Sign in to update profile', { variant: 'warning', duration: 3000 });
        }
      } catch {
        // Silent fail - profile update not critical
      }
    }
  } catch {
    // Silent fail - malformed pending profile data
  }
}
